import { isDefined, isNotDefined } from '@sgme/fp';
import { HStack, Spacer, VStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { format } from 'date-fns';
import deepEqual from 'fast-deep-equal';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { v4 as uuid } from 'uuid';
import * as XLSX from 'xlsx';

import { useCheckBulkTradeWithTradeCapture } from '@/App/BulkTrade/BulkTradeContext.tsx';
import { BulkTradeImportModal } from '@/App/BulkTrade/BulkTradeImportModal.tsx';
import { SgIcon } from '@/components/sg-design-system/components/display/SgIcon';
import { SgButton } from '@/components/sg-design-system/components/input/SgButton';
import CurrencyPairPicker from '@/components/ui/CurrencyPairPicker';
import { checkIfUserInternalSales, useUserContext } from '@/context/UserContext.tsx';
import { selectBulkTrade } from '@/store/bulkTrade/selectors.ts';
import type {
  BulkLeg,
  BulkPricedCashNettedLeg,
  BulkPricedCashSplitLeg,
  BulkTradePriced,
} from '@/store/bulkTrade/state.ts';
import { changeBulkTradeCurrencyPair } from '@/store/bulkTrade/thunks/changeBulkTradeCurrencyPair.ts';
import { computeLegData, getNettedLegByDate } from '@/store/bulkTrade/utilities/margin.ts';
import { getEspStreamId } from '@/store/esp/utils.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import type { ISODate } from '@/types/date.ts';
import { useCurrencyPrecision } from '@/utils/currencies/useCurrencyPrecision.ts';
import { convertPointsToRate } from '@/utils/number-point.ts';

type BulkToolbarProps = {
  tabId: string;
  currentStepIndex: number;
};

export const BulkToolbar = (props: BulkToolbarProps) => {
  const { tabId, currentStepIndex } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const dispatch = useAppDispatch();
  const checkBulkTradeWithTradeCapture = useCheckBulkTradeWithTradeCapture();

  const spot = useAppSelector((state) => {
    const bulkTrade = selectBulkTrade(state, tabId);

    if (isNotDefined(bulkTrade) || isNotDefined(bulkTrade.currencyPair) || isNotDefined(bulkTrade.tiering)) {
      return undefined;
    }

    const espId = getEspStreamId(bulkTrade.currencyPair, bulkTrade.tiering);
    const espStream = state.esp.streams[espId];

    if (espStream?.status !== 'PRICED') {
      return undefined;
    }

    return convertPointsToRate(espStream.midRate, espStream.currencyPairPrecision);
  });

  const bulkTrade = useAppSelector((state) => {
    return selectBulkTrade(state, tabId);
  });

  const precision = useCurrencyPrecision(bulkTrade?.currencyPair);

  const userContext = useUserContext();
  const isSales = checkIfUserInternalSales(userContext);

  const currentBulkTradeId = useAppSelector((state) => {
    const bulkTrade = selectBulkTrade(state, tabId);

    return bulkTrade?.id;
  }, deepEqual);

  const [isImportModalVisible, setImportModalVisible] = useState(false);

  const onCurrencyPairChange = async (newPair: string) => {
    const bulkTradeId = currentBulkTradeId ?? uuid();

    sgwtWebAnalytics?.trackEvent('BulkTrade', 'change currency pair', newPair);

    // Capture current legs before the currency pair change to preserve them.
    // When no existing trade is found, initialize with a single Spot leg.
    const currentLegChanges =
      isDefined(bulkTrade) && bulkTrade.step === 'building'
        ? Object.entries(bulkTrade.legs).reduce(
            (acc, [legId, leg]) => {
              acc[legId] = { type: leg.type };
              return acc;
            },
            {} as Record<string, { type: BulkLeg['type'] }>,
          )
        : { '0': { type: 'Spot' as const } };

    dispatch(changeBulkTradeCurrencyPair({ tabId, bulkTradeId, currencyPair: newPair }));

    checkBulkTradeWithTradeCapture({
      bulkTradeId,
      currencyPair: newPair,
      legChanges: currentLegChanges,
    });
  };

  const handleOpenImportModal = () => {
    sgwtWebAnalytics?.trackEvent('BulkTrade', 'open import modal');

    setImportModalVisible(true);
  };

  const handleCloseImportModal = () => {
    sgwtWebAnalytics?.trackEvent('BulkTrade', 'close import modal');

    setImportModalVisible(false);
  };

  const handleExportData = () => {
    if (isNotDefined(bulkTrade) || bulkTrade.step !== 'priced') {
      return;
    }

    sgwtWebAnalytics?.trackEvent('BulkTrade', 'export data');

    const nettedLegByDate = getNettedLegByDate(bulkTrade.nettedLegs);

    const toExportedLeg = getExportedLeg(bulkTrade, precision, isSales);

    const rows = [
      ['Pricing'],
      ['Type', 'CCY Pair', 'Dealt CCY', 'Way', 'Amount', 'Date', 'Tenor', 'Spot', 'Points', 'All in'],
      ...bulkTrade.nettedLegs.map((leg) => toExportedLeg({ leg, type: 'netted', nettedLegByDate: undefined })),
      [],
      ['Booking'],
      ['Type', 'CCY Pair', 'Dealt CCY', 'Way', 'Amount', 'Date', 'Tenor', 'Spot', 'Points', 'All in'],
      ...bulkTrade.splitLegs.map((leg) => toExportedLeg({ leg, type: 'split', nettedLegByDate })),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(rows);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'BulkTradeData');

    const now = format(new Date(), 'yyyy-MM-dd-HH_mm_ss');
    XLSX.writeFile(workbook, `Export-SGMarketsFX-BulkTrade-${now}.xlsx`);
  };

  return (
    <>
      <HStack gap="12px" verticalAlignment="end" className="mt-4">
        <VStack gap="8px">
          <div className="fs-12 text-secondary">
            <FormattedMessage id="bulkTrade.currencyPair" />
          </div>
          <CurrencyPairPicker value={bulkTrade?.currencyPair} onChange={onCurrencyPairChange} />
        </VStack>

        <VStack gap="8px">
          <div className="fs-12 text-secondary">
            <FormattedMessage id="bulkTrade.midSpot" />
          </div>
          <div style={{ height: 32 }}>
            <span className="fs-21 fs-medium" data-e2e="mid-spot-rate">
              {isDefined(spot) ? spot : '-'}
            </span>
          </div>
        </VStack>

        <Spacer />

        {currentStepIndex === 0 && (
          <SgButton purpose="flat" iconPosition="start" onClick={handleOpenImportModal} data-e2e="import-button">
            <SgIcon icon="attach_file" />
            <FormattedMessage id="bulkTrade.buttons.importData" />
          </SgButton>
        )}

        {(currentStepIndex === 1 || currentStepIndex === 2) && (
          <SgButton purpose="flat" iconPosition="start" onClick={handleExportData} data-e2e="export-button">
            <SgIcon icon="save_alt" />
            <FormattedMessage id="bulkTrade.buttons.exportData" />
          </SgButton>
        )}
      </HStack>

      {isImportModalVisible && <BulkTradeImportModal tabId={tabId} onClose={handleCloseImportModal} />}
    </>
  );
};

function getExportedLeg(bulkTrade: BulkTradePriced, precision: any, isSales: boolean) {
  return ({
    leg,
    type: legType,
    nettedLegByDate,
  }:
    | { type: 'netted'; leg: BulkPricedCashNettedLeg; nettedLegByDate: undefined }
    | { type: 'split'; leg: BulkPricedCashSplitLeg; nettedLegByDate: Record<ISODate, any> }) => {
    const legData = computeLegData(
      leg.quoteLeg,
      {
        spotMargin:
          legType === 'netted'
            ? leg.spotCommercialMarginPPS
            : nettedLegByDate[leg.quoteLeg.Date].spotCommercialMarginPPS,
        forwardMargin:
          leg.quoteLeg.CustomerWay === 'Buy'
            ? Number(leg.quoteLeg.ForwardMarginPoints?.Ask ?? 0)
            : Number(leg.quoteLeg.ForwardMarginPoints?.Bid ?? 0),
      },
      bulkTrade.negotiatedCurrencyIndex,
      precision,
      isSales,
    );

    return [
      leg.quoteLeg.IsForward ? 'FORWARD' : 'SPOT',
      bulkTrade.currencyPair,
      bulkTrade.negotiatedCurrencyIndex === 1 ? bulkTrade.currency1 : bulkTrade.currency2,
      leg.quoteLeg.CustomerWay,
      leg.quoteLeg.Amount,
      convertDateToExcelDate(legData.paymentDate),
      legData.paymentDateTenor,
      legData.spotWithMargin,
      legData.forwardPointsWithMargin,
      legData.allInWithMargin,
    ];
  };
}

function convertDateToExcelDate(date: ISODate): string {
  return format(new Date(date), 'dd/MM/yyyy');
}
